import React, { useMemo, useCallback } from 'react';
import cx from 'classnames';
import { AxisRight } from '@visx/axis';
import { Grid } from '@visx/grid';
import { withParentSize } from '@visx/responsive';
import { scaleLinear } from '@visx/scale';
import { useTooltip } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import SprintTooltip, {
  formatAmount,
  sprintTooltipStyles,
} from '../SprintTooltip';
import Sprint from './Sprint';

import styles from './Churn.module.scss';

const tickLabelPropsFactory = (scale) => (d) => {
  const result = {
    fontSize: 9,
    strokeWidth: 0,
    paintOrder: 'stroke',
    textAnchor: 'start',
    y: scale(d) - 5,
    x: 4,
    className: 'tick-label',
  };

  return result;
};

function tooltipSections({ sprint, metrics }, units) {
  const amount = (value) => formatAmount(value, units);
  const total = (states) =>
    states.todo[units] + states.indeterminate[units] + states.done[units];
  const change = (value, sign) =>
    value ? `${sign}${amount(value)}` : amount(0);
  const { initial, remaining } = metrics;

  return [
    {
      title: 'Work',
      headers: ['Start', sprint.complete_date !== null ? 'End' : 'Current'],
      columns: ['start', 'end'],
      rows: [
        {
          label: 'To Do',
          values: [amount(initial.todo[units]), amount(remaining.todo[units])],
          marker: styles.todo,
        },
        {
          label: 'In Progress',
          values: [
            amount(initial.indeterminate[units]),
            amount(remaining.indeterminate[units]),
          ],
          marker: styles.indeterminate,
        },
        {
          label: 'Done',
          values: [amount(initial.done[units]), amount(remaining.done[units])],
          marker: styles.done,
        },
        {
          label: 'Total',
          values: [amount(total(initial)), amount(total(remaining))],
          total: true,
        },
      ],
    },
    {
      title: 'Changes',
      rows: [
        {
          label: 'Added',
          value: change(metrics.added_and_not_removed[units], '+'),
          marker: styles.added,
        },
        {
          label: 'Growth',
          value: change(metrics.growth[units], '+'),
          marker: styles.growth,
        },
        {
          label: 'Removed',
          value: change(metrics.removed_from_initial[units], '−'),
          marker: styles.removed,
        },
        {
          label: 'Shrinkage',
          value: change(metrics.shrinkage[units], '−'),
          marker: styles.shrinkage,
        },
      ],
    },
  ];
}

function ChurnChart({
  parentWidth: width,
  height = 300,
  margins = { top: 5, right: 20, bottom: 20, left: 20 },
  data,
  sprintCount = 7,
  sprintPaddingPercentage = 0.6,
  units = 'story_points',
}) {
  margins = { top: 20, right: 20, bottom: 20, left: 20, ...margins };
  const chartWidth = width - margins.left - margins.right;
  const chartHeight = height - margins.top - margins.bottom;

  // get the last 7 sprints
  const sprints = useMemo(() => {
    const x = data.slice(-sprintCount).reverse();
    // return [...x, ...x, ...x, ...x].slice(-sprintCount)
    return x;
  }, [data, sprintCount]);

  const { sprintWidth, paddingWidth, barWidth, gapWidth } = useMemo(() => {
    const sprintWidth =
      chartWidth /
      (sprintCount * (1 + sprintPaddingPercentage) + sprintPaddingPercentage);
    const paddingWidth = sprintWidth * sprintPaddingPercentage;
    const barWidth = sprintWidth / 6.5;
    const gapWidth = barWidth * 0.5;
    return {
      sprintWidth,
      paddingWidth,
      barWidth,
      gapWidth,
    };
  }, [chartWidth, sprintCount, sprintPaddingPercentage]);

  const xScale = scaleLinear({
    domain: [0, sprintCount - 1],
    range: [chartWidth - paddingWidth - sprintWidth, paddingWidth],
  });
  const yScale = scaleLinear({
    domain: [
      0,
      1.1 *
        Math.max(
          ...data.flatMap((sprint) => {
            const totalGrowth =
              sprint.metrics.growth[units] +
              sprint.metrics.added_and_not_removed[units];
            return [
              sprint.metrics.completed_total[units] + totalGrowth,
              sprint.metrics.committed[units] + totalGrowth,
            ];
          })
        ) || 1,
    ],
    range: [chartHeight, 0],
  });

  const formatSizeTick = useCallback(
    (size) => {
      if (yScale(size) < 15) {
        return '';
      }
      return size;
    },
    [yScale]
  );

  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const handleSprintHover = useCallback(
    (event, sprint) => {
      const coords = localPoint(event);
      showTooltip({
        tooltipLeft: coords.x,
        tooltipTop: coords.y,
        tooltipData: sprint,
      });
    },
    [showTooltip]
  );

  return (
    <div className={sprintTooltipStyles.chart}>
      <svg viewBox={`0 0 ${width} ${height}`}>
        <g transform={`translate(${margins.left}, ${margins.top})`}>
          <g>
            <Grid
              className="chart-grid chart-neutral-line"
              height={chartHeight}
              width={chartWidth}
              rowTickValues={yScale.ticks(4).filter((tick) => tick > 0)}
              numTicksRows={5}
              numTicksColumns={0}
              xScale={xScale}
              yScale={yScale}
            />
            <AxisRight
              scale={yScale}
              axisClassName="chart-axis"
              tickFormat={formatSizeTick}
              tickLineProps={{ className: 'tick' }}
              tickLabelProps={tickLabelPropsFactory(yScale)}
              numTicks={5}
              tickValues={yScale.ticks(4).filter((tick) => tick > 0)}
            />
          </g>
          <g>
            {sprints.map((sprint, i) => {
              return (
                <g
                  key={i + 'sprint-' + sprint.sprint.id}
                  transform={`translate(${xScale(i)}, 0)`}
                >
                  <Sprint
                    sprint={sprint}
                    barWidth={barWidth}
                    gapWidth={gapWidth}
                    yScale={yScale}
                    chartHeight={chartHeight}
                    units={units}
                  />
                </g>
              );
            })}
          </g>
          <rect
            x="0"
            y="0"
            width={chartWidth}
            height={chartHeight}
            className="chart-box"
          />
          <g>
            {sprints.map((sprint, i) => (
              <rect
                key={i + 'hover-' + sprint.sprint.id}
                x={xScale(i) - paddingWidth / 2}
                y={0}
                width={sprintWidth + paddingWidth}
                height={chartHeight}
                className={cx(sprintTooltipStyles['hover-column'], {
                  [sprintTooltipStyles.hovered]: tooltipData === sprint,
                })}
                onMouseMove={(event) => handleSprintHover(event, sprint)}
                onMouseLeave={hideTooltip}
              />
            ))}
          </g>
        </g>
      </svg>
      {tooltipOpen && tooltipData && (
        <SprintTooltip
          left={tooltipLeft}
          top={tooltipTop}
          sprint={tooltipData.sprint}
          units={units}
          sections={tooltipSections(tooltipData, units)}
          columns={2}
        />
      )}
      <div>
        <div
          className={styles['sprint-names']}
          style={{
            gap: `${paddingWidth}px`,
            margin: `-1rem ${paddingWidth + 15}px 0`,
            '--sprint-count': sprintCount,
          }}
        >
          {sprints.map((s) => (
            <div key={s.sprint.id}>
              {s.sprint.name}
              {s.sprint.complete_date === null && <div>(active)</div>}
            </div>
          ))}
        </div>
      </div>
      <div className="legend-bottom">
        <div className="legend">
          <div className={cx('legend-set', styles['issue-state-legend'])}>
            <div className={'legend-label'}>Start/End State:</div>
            <div className={'legend-item'}>
              <span className={cx('legend-marker', styles.todo)}></span>
              To Do
            </div>
            <div className={'legend-item'}>
              <span
                className={cx('legend-marker', styles.indeterminate)}
              ></span>
              In Progress
            </div>
            <div className={'legend-item'}>
              <span className={cx('legend-marker', styles.done)}></span>
              Done
            </div>
          </div>
          <div className={cx('legend-set', styles['sprint-start-end-legend'])}>
            <div className={'legend-label'}>Start/End:</div>
            <div className={'legend-item'}>
              <span className={cx('legend-marker', styles.start)}></span>
              Sprint Start
            </div>
            <div className={'legend-item'}>
              <span className={cx('legend-marker', styles.end)}></span>
              Sprint End
            </div>
            <div className={'legend-item'}>
              <span className={cx('legend-marker', styles.current)}></span>
              Current
            </div>
          </div>
          <div className={cx('legend-set', styles['issue-delta-legend'])}>
            <div className={'legend-label'}>Changes:</div>
            <div className={'legend-item'}>
              <span className={cx('legend-marker', styles.added)}></span>
              Added
            </div>
            <div className={'legend-item'}>
              <span className={cx('legend-marker', styles.growth)}></span>
              Growth
            </div>
            <div className={'legend-item'}>
              <span className={cx('legend-marker', styles.removed)}></span>
              Removed
            </div>
            <div className={'legend-item'}>
              <span className={cx('legend-marker', styles.shrinkage)}></span>
              Shrinkage
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withParentSize(ChurnChart);
